<div class="template-list-dialog">
    <!-- Header -->
    <div class="section-header" mat-dialog-title>
        <mat-icon class="section-icon">dashboard_customize</mat-icon>
        <span class="section-title">{{ 'ai.templateBrowser' | transloco }}</span>
        <span class="template-count-badge">{{ filteredTemplates.length }}</span>
        <button mat-icon-button class="close-button" mat-dialog-close>
            <mat-icon>close</mat-icon>
        </button>
    </div>

    <!-- Filters Bar -->
    <div class="filters-bar">
        <!-- Search -->
        <div class="search-box">
            <mat-icon>search</mat-icon>
            <input type="text"
                   [(ngModel)]="searchQuery"
                   (ngModelChange)="onSearchChange()"
                   [placeholder]="'ai.search' | transloco">
            @if (searchQuery) {
                <button mat-icon-button class="clear-button" (click)="clearSearch()">
                    <mat-icon>close</mat-icon>
                </button>
            }
        </div>

        <!-- Orientation Toggles -->
        <div class="orientation-toggles">
            <button mat-icon-button
                    class="orient-btn"
                    [class.active]="activeOrientation === 'landscape'"
                    (click)="setOrientation('landscape')"
                    [matTooltip]="'ai.landscape' | transloco">
                <mat-icon>crop_landscape</mat-icon>
            </button>
            <button mat-icon-button
                    class="orient-btn"
                    [class.active]="activeOrientation === 'portrait'"
                    (click)="setOrientation('portrait')"
                    [matTooltip]="'ai.portrait' | transloco">
                <mat-icon>crop_portrait</mat-icon>
            </button>
            <button mat-icon-button
                    class="orient-btn"
                    [class.active]="activeOrientation === 'square'"
                    (click)="setOrientation('square')"
                    [matTooltip]="'ai.square' | transloco">
                <mat-icon>crop_square</mat-icon>
            </button>
        </div>
    </div>

    <!-- Category Chips -->
    <div class="category-chips">
        @for (cat of categories; track cat.key) {
            <button class="chip"
                    [class.active]="activeCategory === cat.key"
                    (click)="toggleCategory(cat.key)">
                <mat-icon>{{ cat.icon }}</mat-icon>
                <span class="chip-label">{{ cat.name }}</span>
                <span class="chip-count">{{ cat.count }}</span>
            </button>
        }
    </div>

    <!-- Templates Grid -->
    <div mat-dialog-content
         class="templates-grid-content"
         (scroll)="onScroll($event)"
         #scrollContainer>
        @if (filteredTemplates.length === 0) {
            <div class="empty-state">
                <mat-icon>search_off</mat-icon>
                <p>{{ 'ai.noTemplatesFound' | transloco }}</p>
            </div>
        }

        <div class="templates-grid">
            @for (template of visibleTemplates; track template.id; let i = $index) {
                <div class="template-card"
                     [class.selected]="selectedTemplate?.id === template.id"
                     (click)="selectTemplate(template)">
                    <!-- Template Number Badge -->
                    <div class="template-number-badge">{{ i + 1 }}</div>

                    <!-- Template Preview -->
                    <div class="template-preview">
                        <div class="preview-canvas">
                            @for (block of template.blocks; track $index) {
                                <div class="preview-block block-{{block.type}}"
                                     [class.rounded]="block.rounded"
                                     [class.special]="block.special"
                                     [style.left.%]="block.x"
                                     [style.top.%]="block.y"
                                     [style.width.%]="block.w"
                                     [style.height.%]="block.h">

                                    <!-- Text blocks (header, title, text) -->
                                    @if (block.type === 'header' || block.type === 'title' || block.type === 'text') {
                                        @for (line of [].constructor(block.lines || 3); track $index) {
                                            <div class="line"
                                                 [style.width.%]="block.type === 'text' ?
                                                    [95, 82, 88, 70, 93, 78, 85, 75, 90, 80][$index % 10] :
                                                    95 - ($index * 5)"
                                                 [style.margin-bottom.px]="block.type === 'text' && $index % 3 === 2 ? 4 : 0"></div>
                                        }
                                    }

                                    <!-- Bullets -->
                                    @if (block.type === 'bullets') {
                                        @for (bullet of [].constructor(block.lines || 4); track $index) {
                                            <div class="bullet-item">
                                                <div class="dot"></div>
                                                <div class="line" [style.width.%]="90 - ($index * 8)"></div>
                                            </div>
                                        }
                                    }

                                    <!-- Image -->
                                    @if (block.type === 'image') {
                                        <mat-icon>image</mat-icon>
                                    }

                                    <!-- Icon -->
                                    @if (block.type === 'icon') {
                                        <mat-icon>{{ block.iconType || 'star' }}</mat-icon>
                                    }

                                    <!-- Video / MimeType -->
                                    @if (block.type === 'video' || block.type === 'mimeType') {
                                        <mat-icon>{{ block.isMimeType ? 'widgets' : 'play_circle' }}</mat-icon>
                                    }

                                    <!-- Chart -->
                                    @if (block.type === 'chart') {
                                        <mat-icon>{{ block.chartType === 'pie' ? 'pie_chart' : 'bar_chart' }}</mat-icon>
                                    }

                                    <!-- Table -->
                                    @if (block.type === 'table') {
                                        @for (cell of [].constructor(9); track $index) {
                                            <div class="cell"></div>
                                        }
                                    }

                                    <!-- Special blocks -->
                                    @if (block.type === 'pyramid') {
                                        <mat-icon>layers</mat-icon>
                                    }
                                    @if (block.type === 'timeline') {
                                        <mat-icon>timeline</mat-icon>
                                    }
                                    @if (block.type === 'progress') {
                                        <mat-icon>trending_up</mat-icon>
                                    }

                                    <!-- Button -->
                                    @if (block.type === 'button') {
                                        <span>BUTTON</span>
                                    }

                                    <!-- Textbox -->
                                    @if (block.type === 'textbox') {
                                        <div class="placeholder"></div>
                                    }

                                    <!-- Field (JSON data items) -->
                                    @if (block.type === 'field') {
                                        <div class="field-line"></div>
                                    }
                                </div>
                            }
                        </div>
                    </div>

                    <!-- Template Info -->
                    <div class="template-info">
                        <h4>{{ template.name }}</h4>
                        <div class="blocks-count">
                            <mat-icon>widgets</mat-icon>
                            {{ template.blocks.length }}
                        </div>
                    </div>
                </div>
            }
        </div>

        <!-- Load More Sentinel -->
        @if (canLoadMore) {
            <div class="load-more-section">
                <button mat-button class="load-more-btn" (click)="loadMore()">
                    <mat-icon>expand_more</mat-icon>
                    {{ 'ai.loadMore' | transloco }}
                    <span class="remaining-count">({{ filteredTemplates.length - visibleTemplates.length }})</span>
                </button>
            </div>
        }
    </div>

    <!-- Footer Actions -->
    <div class="footer-actions">
        <div class="footer-info">
            {{ visibleTemplates.length }} / {{ filteredTemplates.length }} {{ 'ai.templates' | transloco }}
        </div>
        <div class="action-buttons">
            <button mat-button (click)="cancel()">
                {{ 'general.cancel' | transloco }}
            </button>
            <button mat-raised-button
                    color="warn"
                    (click)="applyTemplate()"
                    [disabled]="!selectedTemplate || loading">
                @if (loading) {
                    <mat-spinner diameter="20"></mat-spinner>
                } @else {
                    <mat-icon>check</mat-icon>
                }
                {{ 'ai.applyTemplate' | transloco }}
            </button>
        </div>
    </div>
</div>
